/**
 * Enhanced token metadata fetcher with Jupiter Token List fallback
 * Optimized for performance with IndexedDB caching and verified tokens only
 */
import { getMetaplexMetadata } from './metaplexMetadata';
import { getValidImageUrl } from './imageValidator';

interface JupiterToken {
  address: string;
  chainId: number;
  decimals: number;
  name: string;
  symbol: string;
  logoURI?: string;
  tags?: string[];
  extensions?: {
    coingeckoId?: string;
  };
}

interface SolanaToken {
  address: string;
  chainId: number;
  decimals: number;
  name: string;
  symbol: string;
  logoURI?: string;
}

// Memory cache for token lists (fast access)
let jupiterTokensCache: Map<string, JupiterToken> | null = null;
let solanaTokensCache: Map<string, SolanaToken> | null = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours (increased from 5 minutes)
const TOKEN_LIST_RETRY_DELAY = 5 * 60 * 1000; // 5 minutes
let solanaTokensPromise: Promise<Map<string, SolanaToken>> | null = null;
let solanaTokensLastFailureAt = 0;

// IndexedDB configuration
const DB_NAME = 'junk-fun-tokens';
const DB_VERSION = 1;
const STORE_NAME = 'tokens';
const INDEXEDDB_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Initialize IndexedDB for token caching
 */
async function openTokenDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
}

/**
 * Get tokens from IndexedDB cache
 */
async function getTokensFromIndexedDB(key: string): Promise<JupiterToken[] | null> {
  try {
    const db = await openTokenDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.get(key);
      request.onsuccess = () => {
        const data = request.result;
        if (data && data.timestamp && Date.now() - data.timestamp < INDEXEDDB_CACHE_DURATION) {
          console.log(`✅ Loaded ${data.tokens.length} tokens from IndexedDB cache`);
          resolve(data.tokens);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.warn('⚠️ Failed to read from IndexedDB:', error);
    return null;
  }
}

/**
 * Save tokens to IndexedDB cache
 */
async function saveTokensToIndexedDB(key: string, tokens: JupiterToken[]): Promise<void> {
  try {
    const db = await openTokenDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    store.put({ tokens, timestamp: Date.now() }, key);
    console.log(`💾 Saved ${tokens.length} tokens to IndexedDB`);
  } catch (error) {
    console.warn('⚠️ Failed to save to IndexedDB:', error);
  }
}

/**
 * Fetch and cache Jupiter token list (verified tokens only for performance)
 * Uses IndexedDB for persistent caching across sessions
 *
 * NOTE: Currently commented out due to DNS resolution issues
 * Can be re-enabled later if needed
 */
/* eslint-disable @typescript-eslint/no-unused-vars */
async function getJupiterTokens(): Promise<Map<string, JupiterToken>> {
  const now = Date.now();
  const cacheKey = 'jupiter-verified-tokens';

  // Return memory cache if still valid
  if (jupiterTokensCache && now - cacheTimestamp < CACHE_DURATION) {
    console.log(`✅ Using memory cache (${jupiterTokensCache.size} tokens)`);
    return jupiterTokensCache;
  }

  // Try IndexedDB cache
  const cachedTokens = await getTokensFromIndexedDB(cacheKey);
  if (cachedTokens) {
    jupiterTokensCache = new Map(cachedTokens.map(t => [t.address, t]));
    cacheTimestamp = now;
    return jupiterTokensCache;
  }

  try {
    // Use verified tokens endpoint - much smaller payload (~500 tokens vs 10,000+)
    const response = await fetch('https://tokens.jup.ag/tokens?tags=verified', {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const tokens: JupiterToken[] = await response.json();

    jupiterTokensCache = new Map(
      tokens.map(t => [t.address, t])
    );
    cacheTimestamp = now;

    // Save to IndexedDB for persistent caching
    await saveTokensToIndexedDB(cacheKey, tokens);

    return jupiterTokensCache;
  } catch (error) {
    console.error('Failed to fetch Jupiter tokens:', error);
    // Return stale cache if available (stale-while-revalidate pattern)
    return jupiterTokensCache || new Map();
  }
}
/* eslint-enable @typescript-eslint/no-unused-vars */

/**
 * Fetch and cache Solana token list (mainnet only, devnet doesn't have a token list)
 */
async function getSolanaTokens(network: 'mainnet' | 'devnet' = 'mainnet'): Promise<Map<string, SolanaToken>> {
  const now = Date.now();
  
  // Return cached data if still valid
  if (solanaTokensCache && now - cacheTimestamp < CACHE_DURATION) {
    return solanaTokensCache;
  }

  // Devnet doesn't have a token list on Solana Labs GitHub - skip for devnet
  if (network === 'devnet') {
    console.log('ℹ️ Skipping Solana token list for devnet (not available)');
    return new Map();
  }

  if (solanaTokensPromise) {
    return solanaTokensPromise;
  }

  if (solanaTokensLastFailureAt && now - solanaTokensLastFailureAt < TOKEN_LIST_RETRY_DELAY) {
    return solanaTokensCache || new Map();
  }

  solanaTokensPromise = (async () => {
    try {
      const url = 'https://raw.githubusercontent.com/solana-labs/token-list/main/src/tokens/solana.tokenlist.json';

      console.log(`📥 Fetching Solana ${network} token list...`);
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      solanaTokensCache = new Map(
        data.tokens.map((t: SolanaToken) => [t.address, t])
      );
      cacheTimestamp = now;
      solanaTokensLastFailureAt = 0;

      console.log(`✅ Loaded ${solanaTokensCache.size} tokens from Solana token list`);
      return solanaTokensCache;
    } catch (error) {
      solanaTokensLastFailureAt = Date.now();
      console.error('❌ Failed to fetch Solana tokens:', error);
      return solanaTokensCache || new Map();
    } finally {
      solanaTokensPromise = null;
    }
  })();

  return solanaTokensPromise;
}

/**
 * Try to fetch token metadata from DexScreener API (great for newer tokens)
 * Public API, no key required
 */
async function getDexScreenerTokenMetadata(mint: string): Promise<{
  name?: string;
  symbol?: string;
  logoURI?: string;
  decimals?: number;
} | null> {
  try {
    // DexScreener public API endpoint
    const url = `https://api.dexscreener.com/latest/dex/tokens/${mint}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();

    // DexScreener returns pairs array, we need to extract token info
    if (data.pairs && data.pairs.length > 0) {
      const pair = data.pairs[0];
      const baseToken = pair.baseToken;

      if (baseToken && baseToken.address === mint) {
        // DexScreener provides images in the pair info
        // Try multiple possible locations for the image
        const imageUrl =
          pair.info?.imageUrl ||
          baseToken.info?.imageUrl ||
          pair.imageUrl ||
          baseToken.imageUrl ||
          pair.image ||
          baseToken.image;

        // Apply validation to filter out problematic domains
        const validImageUrl = getValidImageUrl(imageUrl);

        return {
          name: baseToken.name,
          symbol: baseToken.symbol,
          logoURI: validImageUrl, // Use validated image URL (undefined if blocked)
          decimals: undefined // Not provided by DexScreener
        };
      }
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Try to fetch token metadata from Birdeye API (alternative source)
 * Free tier allows basic token info queries
 */
async function getBirdeyeTokenMetadata(mint: string): Promise<{
  name?: string;
  symbol?: string;
  logoURI?: string;
  decimals?: number;
} | null> {
  try {
    // Birdeye public API endpoint (no API key needed for basic info)
    const url = `https://public-api.birdeye.so/public/token_metadata?address=${mint}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();

    if (data.success && data.data) {
      // Apply validation to filter out problematic domains
      const imageUrl = data.data.logoURI || data.data.logo;
      const validImageUrl = getValidImageUrl(imageUrl);

      return {
        name: data.data.name,
        symbol: data.data.symbol,
        logoURI: validImageUrl,
        decimals: data.data.decimals
      };
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Get enhanced token metadata with multiple fallbacks
 * Priority: Jupiter (best logos) -> Solana Token List -> DexScreener -> Birdeye -> null
 * DexScreener and Birdeye work well for newer tokens not in official registries
 */
export async function getEnhancedTokenMetadata(
  mint: string,
  network: 'mainnet' | 'devnet' = 'mainnet',
  options: {
    allowFallbackLookups?: boolean;
  } = {}
): Promise<{
  name?: string;
  symbol?: string;
  logoURI?: string;
  decimals?: number;
} | null> {
  try {
    const allowFallbackLookups = options.allowFallbackLookups !== false;

    // Skip Jupiter API - it's having DNS resolution issues
    // Comment out for now, can re-enable later if needed
    /*
    if (network === 'mainnet') {
      const jupiterTokens = await getJupiterTokens();
      const jupiterToken = jupiterTokens.get(mint);

      if (jupiterToken) {
        console.log(`✅ Found ${jupiterToken.symbol} in Jupiter token list`);
        return {
          name: jupiterToken.name,
          symbol: jupiterToken.symbol,
          logoURI: jupiterToken.logoURI,
          decimals: jupiterToken.decimals,
        };
      }
    }
    */

    // Start with Solana token list (includes devnet)
    const solanaTokens = await getSolanaTokens(network);
    const solanaToken = solanaTokens.get(mint);
    
    if (solanaToken) {
      console.log(`✅ Found ${solanaToken.symbol} in Solana token list`);
      // Apply validation to filter out problematic domains
      const validImageUrl = getValidImageUrl(solanaToken.logoURI);

      return {
        name: solanaToken.name,
        symbol: solanaToken.symbol,
        logoURI: validImageUrl,
        decimals: solanaToken.decimals,
      };
    }

    if (!allowFallbackLookups) {
      return null;
    }

    // Try DexScreener first (fast and reliable for newer tokens)
    if (network === 'mainnet') {
      const dexScreenerMetadata = await getDexScreenerTokenMetadata(mint);
      if (dexScreenerMetadata) {
        return dexScreenerMetadata;
      }
    }

    // Try Birdeye as a fallback (works for some tokens not in registries)
    if (network === 'mainnet') {
      const birdeyeMetadata = await getBirdeyeTokenMetadata(mint);
      if (birdeyeMetadata) {
        return birdeyeMetadata;
      }
    }

    // Try Metaplex on-chain metadata as a last resort
    // This works for tokens with on-chain metadata
    if (network === 'mainnet') {
      const metaplexMetadata = await getMetaplexMetadata(mint);
      if (metaplexMetadata) {
        console.log(`✅ Found ${metaplexMetadata.symbol} in Metaplex metadata`);
        // Apply validation to filter out problematic domains
        const validImageUrl = getValidImageUrl(metaplexMetadata.imageUrl);

        return {
          name: metaplexMetadata.name,
          symbol: metaplexMetadata.symbol,
          logoURI: validImageUrl,
          decimals: undefined,
        };
      }
    }

    // Return minimal metadata with the mint address for identification
    // This helps users identify tokens even without registry data
    return null;
  } catch (error) {
    console.error(`❌ Error fetching metadata for ${mint}:`, error);
    return null;
  }
}

/**
 * Clear the token metadata cache (useful for testing or switching networks)
 * Clears both memory cache and IndexedDB
 */
export async function clearTokenCache() {
  jupiterTokensCache = null;
  solanaTokensCache = null;
  cacheTimestamp = 0;
  solanaTokensPromise = null;
  solanaTokensLastFailureAt = 0;

  if (typeof indexedDB === 'undefined') {
    console.log('🗑️ Token metadata memory cache cleared');
    return;
  }

  // Clear IndexedDB cache
  try {
    const db = await openTokenDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    store.clear();
    console.log('🗑️ Token metadata cache cleared (memory + IndexedDB)');
  } catch (error) {
    console.warn('⚠️ Failed to clear IndexedDB cache:', error);
    console.log('🗑️ Token metadata memory cache cleared');
  }
}
